import styled, {css} from 'styled-components';

/*
 * Nothing in this file may make an element the containing block for fixed-position descendants:
 * no transform, filter, backdrop-filter, perspective, layout/paint containment, container-type,
 * or will-change naming any of those. The line dropdowns open as fixed-position lists inside this
 * scroller, and once an ancestor becomes their containing block the scroller clips them.
 */

/**
 * Only the line table scrolls sideways; the rest of the window stays put. The space at the foot
 * keeps the scrollbar off the last line.
 */
export const LinesScroller = styled.div`
	overflow-x: auto;
	max-width: 100%;
	padding-bottom: ${({theme}) => theme.sys.spacing['300']};
`;

/**
 * One grid for the heading and every line, so a column has the same width in every row. When the
 * row fits, the width left over is shared between the columns' gaps, so the row runs to the right
 * edge without changing any column's width; when it does not fit, there is none and the table
 * scrolls.
 */
export const LinesGrid = styled.div<{$template: string}>`
	display: grid;
	grid-template-columns: ${({$template}) => $template};
	column-gap: ${({theme}) => theme.sys.spacing['300']};
	row-gap: ${({theme}) => theme.sys.spacing['200']};
	align-items: center;
	justify-content: space-between;
	width: max-content;
	min-width: 100%;
`;

/** A row that lays its cells straight onto the grid, so each one takes the grid's column. */
export const LinesRow = styled.div`
	display: contents;
`;

/** One cell of the line table. Every cell scrolls with its row; none is pinned. */
export const LineCell = styled.div<{$numeric?: boolean; $maxWidth?: number}>`
	min-width: 0;
	/* A column's cap holds against its heading too, so a long heading is cut off rather than widening it. */
	${({$maxWidth}) => $maxWidth !== undefined && `max-width: ${$maxWidth}px;`}
	${({$numeric}) =>
		$numeric &&
		css`
			text-align: right;
			justify-self: stretch;
		`}
`;

/** Quantities and amounts, in the numerals type style and the main text colour. */
export const Numeral = styled.span`
	${({theme}) => theme.sys.font.numerals.regular['2']};
	color: ${({theme}) => theme.sys.color.text.content.main};
	white-space: nowrap;
`;

/**
 * Holds a dropdown field without letting the field's own content widen the column: the column's
 * width comes from the sizer in its heading alone.
 */
export const FieldCell = styled.div`
	width: 0;
	min-width: 100%;
`;

/**
 * Sets a dropdown column's width to its widest option, capped at the column's maximum. It holds
 * one field per option, drawn as the real field would be, and is never seen.
 */
export const ColumnSizer = styled.div<{$maxWidth: number}>`
	width: max-content;
	max-width: ${({$maxWidth}) => $maxWidth}px;
	height: 0;
	overflow: hidden;
	visibility: hidden;
`;

/** A sizer label drawn from its attribute, so the hidden copy is not page text. */
export const SizerLabel = styled.span`
	&::before {
		content: attr(data-label);
		white-space: nowrap;
	}
`;
